<template>
  <div class="reset-password">
    <div class="reset-password__title">
      <h1>
        {{ $t(
          isFirstConnection
            ? 'resetPassword.title.first'
            : 'resetPassword.title.normal')
        }}
      </h1>
    </div>
    <el-form class="reset-password__form"
      v-if="!isInvalidKey"
      :model="form"
      :rules="rules"
      @submit.native.prevent="submit"
      ref="form">
      <el-form-item
        :label="$t('resetPassword.login')"
        class="reset-password__form__input"
        prop="login">
        <el-input
          :placeholder="$t('resetPassword.login')"
          autofocus
          name="username"
          v-model="form.login"
          suffix-icon="el-icon-message">
        </el-input>
      </el-form-item>
      <el-form-item
        :label="$t('resetPassword.password')"
        class="reset-password__form__input"
        prop="password">
        <el-input
          :placeholder="$t('resetPassword.password')"
          autofocus
          show-password
          v-model="form.password"
          suffix-icon="el-icon-lock">
        </el-input>
      </el-form-item>
      <el-form-item
        :label="$t('resetPassword.passwordConfirmation')"
        class="reset-password__form__input"
        prop="passwordConfirmation">
        <el-input
          :placeholder="$t('resetPassword.passwordConfirmation')"
          show-password
          v-model="form.passwordConfirmation"
          suffix-icon="el-icon-lock">
        </el-input>
      </el-form-item>
      <el-form-item class="reset-password__form__rules">
        <p>{{ $t('resetPassword.rules.title') }}</p>
        <ul v-if="isMinarm">
          <li>{{ $t('resetPassword.rules.lengthCondition') }}</li>
          <li>{{ $t('resetPassword.rules.caseCondition') }}</li>
          <li>{{ $t('resetPassword.rules.specialCondition') }}</li>
        </ul>
        <ul v-else>
          <li>{{ $t('resetPassword.rules.lengthCondition2') }}</li>
        </ul>
      </el-form-item>
      <el-form-item class="reset-password__form__submit">
        <el-button
          :disabled="isResetPasswordLoading || isPasswordKeyLoading"
          :loading="isResetPasswordLoading || isPasswordKeyLoading"
          native-type="submit"
          type="primary"
          class="reset-password__form__submit--btn">
          {{ $t(
            isFirstConnection
              ? 'resetPassword.submit.first'
              : 'resetPassword.submit.normal')
          }}
        </el-button>
      </el-form-item>
      <div class="reset-password__form__login">
        <router-link
          :to="{ name: 'login'}"
          class="reset-password__form__login--link el-link el-link--primary is-underline">
          <span class="el-link--inner">{{ $t('resetPassword.loginPage') }}</span>
        </router-link>
      </div>
      <div class="reset-password__form__register" v-if="isRegisterPossible">
        <p>{{ $t('resetPassword.registerText') }}
          <el-link
            type="primary"
            href="#"
            class="reset-password__form__register--link">
            {{ $t('resetPassword.registerBtn') }}
          </el-link>
        </p>
      </div>
    </el-form>
    <div v-else>
      <el-alert
        class="reset-password__alert"
        type="error"
        :closable="false"
        show-icon>
        <h3
          slot="title"
          class="reset-password__alert__title">
          {{ $t('resetPassword.alert.title') }}
        </h3>
        <p
          class="reset-password__alert__message">
          {{ $t('resetPassword.alert.message') }}
        </p>
      </el-alert>
      <router-link
        :to="{ name: 'recoverPassword'}">
        <div class="el-button el-button--primary reset-password__button">
          <span class="el-button--inner">{{ $t('resetPassword.recoverPassword') }}</span>
        </div>
      </router-link>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import store from '@/store';
import logoNextories from '@/assets/img/logo-nextories.png';

export default {
  name: 'resetPassword',
  data() {
    const self = this;
    const regexValidation = (rule, value, callback) => {
      if (!self.isMinarm || rule.regex.test(value)) {
        return callback();
      }
      return callback(new Error());
    };
    const lengthValidation = (rule, value, callback) => {
      if ((self.isMinarm && value.length) > 9
          || (!self.isMinarm && value.length) > 7) {
        return callback();
      }
      return callback(self.isMinarm
        ? self.$t('resetPassword.lengthCondition')
        : self.$t('resetPassword.lengthCondition2'));
    };
    const equalPasswordValidation = (rule, value, callback) => {
      if (self.form.password !== value) {
        return callback(new Error());
      }
      return callback();
    };
    return {
      isInvalidKey: false,
      form: {
        login: '',
        password: '',
        passwordConfirmation: '',
      },
      rules: {
        login: [
          {
            required: true,
            type: 'email',
            message: this.$t('resetPassword.loginWarning'),
            trigger: 'blur',
          },
        ],
        password: [
          {
            required: true,
            message: this.$t('resetPassword.passwordWarning'),
            trigger: 'blur',
          },
          {
            validator: lengthValidation,
            trigger: 'blur',
          },
          {
            validator: regexValidation,
            regex: /^(?=.*[a-z])(?=.*[A-Z]).+$/,
            message: this.$t('resetPassword.caseCondition'),
            trigger: 'blur',
          },
          {
            validator: regexValidation,
            regex: /[ !@#$%^&*()_+\-=[\]{};':"|,.<>/?]/,
            message: this.$t('resetPassword.specialCondition'),
            trigger: 'blur',
          },
        ],
        passwordConfirmation: [
          {
            required: true,
            message: this.$t('resetPassword.passwordConfirmationWarning'),
            trigger: 'blur',
          },
          {
            validator: equalPasswordValidation,
            message: this.$t('resetPassword.passwordConfirmationEqual'),
            trigger: 'blur',
          },
        ],
      },
    };
  },
  computed: {
    ...mapGetters({
      isMinarm: 'authStore/isMinarm',
      isResetPasswordLoading: 'authStore/isResetPasswordLoading',
      isPasswordKeyLoading: 'authStore/isPasswordKeyLoading',
    }),
    key() {
      return this.$route.query.key;
    },
    type() {
      return this.$route.query.type;
    },
    isFirstConnection() {
      return this.type === 'first';
    },
    logo() {
      return logoNextories;
    },
    isRegisterPossible() {
      return process.env.VUE_APP_REGISTER !== 'false';
    },
  },
  methods: {
    ...mapActions({
      checkPasswordKey: 'authStore/checkPasswordKey',
      resetPassword: 'authStore/resetPassword',
      login: 'authStore/login',
      getProjects: 'projectStore/getProjects',
      getDemand: 'demandStore/getDemand',
    }),
    submit() {
      this.$refs.form.validate(async (valid) => {
        try {
          if (valid) {
            await this.resetPassword({
              email: this.form.login,
              new_password: this.form.password,
              key: this.key,
            });
            await this.getProjects();
            await this.getDemand();
            this.$router.push({ name: 'home' });
          }
          // eslint-disable-next-line no-empty
        } catch (error) {}
      });
    },
  },
  beforeRouteEnter(to, from, next) {
    if (!store.getters['authStore/isLogged']) {
      return next();
    }
    if (to.query.u) {
      return next(decodeURIComponent(to.query.u));
    }
    return next({ name: 'home' });
  },
  created() {
    if (!this.key) {
      this.$router.push({ name: 'login' });
    }
    this.checkPasswordKey({ key: this.key }).catch(() => {
      this.isInvalidKey = true;
    });
  },
};
</script>

<style scoped lang="scss">

.reset-password {
  width: 100%;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 20px;
  @include breakpoint(desktop) {
    padding: 0;
    margin-top: 4rem;
  }
  &__form {
    margin: 20px 0;
    &__input {
      :deep(.el-form-item__label) {
        font-size: 18px;
      }
    }
    &__login {
      text-align: right;
      margin-top: 10px;
      &--link {
        font-size: 16px;
        vertical-align: top;
      }
    }
    &__submit {
      margin-top: 20px;
      margin-bottom: 0;
      &--btn {
        width: 100%;
        font-weight: 700;
        font-size: 18px;
      }
    }
    &__register {
      &--link {
        font-size: 16px;
        vertical-align: top;
      }
    }
    &__rules {
      margin-bottom: 0;
    }
  }
  &__button {
    width: 100%;
    margin-top: 20px;
  }
}
:deep(.reset-password__alert__message) {
  font-size: 16px;
}
:deep(.el-form-item__content) {
  line-height: normal;
}
</style>
